import { UserPrivileges } from 'common/store/session/constants';

import { Filter, NestedFilterObject, SearchFilters } from 'common/store/common/search/types';
import commonSearchQueryBuilder from 'common/services/search/commonSearchQueryBuilder';
import commonFilteringService, { AppSpecifiedFilterService } from 'common/services/commonFilteringService';
import { getWorkspaceType, isWorkflowOverlayOpen } from 'common/services/permissions/commonPermissionService';
import { MapObject } from 'common/types/general';
import store from '../store';
import { RootState } from '../store/rootReducer';
import gradingPartyJson from '../../docs/definitions/filters/gradingParty.json';
import userQuestionJson from '../../docs/definitions/filters/userQuestion.json';
import assessmentRunQuestionJson from '../../docs/definitions/filters/assessmentRunQuestion.json';
import assessmentRunJson from '../../docs/definitions/filters/assessmentRun.json';
import allGradingReadyIssueJson from '../../docs/definitions/filters/allGradingReadyIssue.json';
import {
    ASSESSMENTRUNQUESTION_INDEX,
    GRADING_PARTY_INDEX,
    USERQUESTION_INDEX,
    GRADINGREADY_ISSUE, ASSESSMENTRUN_INDEX,
} from '../store/common/search/constants';
import { hasCurrentUserEvaluatorPrivilege } from './permissions/PermissionService';
import { Committee, isCurrentUserEvaluatorInSelectedCommittee } from './committeeService';
import { CommitteeType } from '../store/assessment/constants';
import { GRADING_MODE } from './workflowService';

const patchEvaluatorRoundsNestedFiltersWithCommittee = (nestedFilter: any, selectedCommitteeId: number) => {
    let patchedFilter;
    if (Array.isArray(nestedFilter)) {
        patchedFilter = nestedFilter.map((nesterFilterObject) => {
            let patchedNesterFilterObject;
            if (nesterFilterObject.$path === 'evaluationRounds') {
                patchedNesterFilterObject = { ...nesterFilterObject, 'evaluationRounds.committeeId': [selectedCommitteeId] };
            }
            return patchedNesterFilterObject || nesterFilterObject;
        });
    } else if (nestedFilter.$path === 'evaluationRounds') {
        patchedFilter = { ...nestedFilter, 'evaluationRounds.committeeId': [selectedCommitteeId] };
    }
    return patchedFilter || nestedFilter;
};

const filteringService: AppSpecifiedFilterService = {
    addAdditionalNestedField(nestedFilter: NestedFilterObject, filter: Filter, additionalContext: any): NestedFilterObject {
        if (filter.additionalNestedValue === 'selectedCommitteeId') {
            if (additionalContext.committees && additionalContext.committees.length) {
                if (additionalContext.selectedCommitteeId > 0) {
                    nestedFilter[filter.additionalNestedField] = additionalContext.selectedCommitteeId;
                } else {
                    nestedFilter[filter.additionalNestedField] = additionalContext.committees
                        .filter((committee: Committee) => {
                            if (filter.onlyAppeals) {
                                return committee.type === CommitteeType.reassessment;
                            }
                            return committee.type === CommitteeType.grading;
                        })
                        .map((committee: Committee) => committee.committeeId);
                }
            }
        } else {
            nestedFilter[filter.additionalNestedField] = filter.additionalNestedValue;
        }
        return nestedFilter;
    },
    addInternalFilters(filters: SearchFilters, dataType: string): SearchFilters {
        const { assessment, context, session } = store.getState() as RootState;
        const { selectedCommitteeId, assessmentRunId } = context;
        const { user } = session;
        const nestedPath: string = this.getNestedPropertyPath(dataType);
        const evaluatorIdField = `${nestedPath}.evaluatorId`;
        const currentEvaluatorFilter = commonSearchQueryBuilder.createNestedFilter(
            evaluatorIdField,
            [user.id, assessment.collaborativeEvaluatorId],
        );
        const userIsGrader: boolean = hasCurrentUserEvaluatorPrivilege();
        let useEvaluatorIdFilter = userIsGrader;
        if (userIsGrader && assessment.committees && assessment.committees.length) {
            if (selectedCommitteeId > 0) {
                useEvaluatorIdFilter = isCurrentUserEvaluatorInSelectedCommittee();
            } else {
                // a planner in the all committees context so don't use grader filters
                useEvaluatorIdFilter = false;
            }
        }
        const isAPlannerWorkspace: boolean = getWorkspaceType() === UserPrivileges.ar_plan;
        let query: any = { ...filters };

        // Helper function to handle evaluatorHasAllQuestionsMarkedCustomKey filter transformation
        const filterKey = 'evaluatorHasAllQuestionsMarkedCustomKey';
        const transformEvaluatorFilter = (filter: MapObject) => {
            if (filter[filterKey] !== undefined) {
                const evaluatorIds = [];
                const applicableSearchUserId = assessment.marksMode === GRADING_MODE.INDIVIDUAL ? user.id : assessment.collaborativeEvaluatorId;
                evaluatorIds.push(`${applicableSearchUserId}`);
                if (selectedCommitteeId) {
                    evaluatorIds.push(`${selectedCommitteeId}_${applicableSearchUserId}`);
                }

                const markingStatusCompleteEvaluatorsKey = 'markingStatus.complete_evaluators';
                const filterValue = filter[filterKey];

                if (filterValue === '$exists') {
                    filter[markingStatusCompleteEvaluatorsKey] = evaluatorIds;
                } else if (filterValue === '$missing') {
                    if (!filter.must_not) {
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        filter.must_not = {};
                    }
                    filter.must_not[markingStatusCompleteEvaluatorsKey] = evaluatorIds;
                }
                // Remove the original custom filter object since we've handled it
                delete filter[filterKey];
            }
            return filter;
        };

        // Handle special case for evaluatorHasAllQuestionsMarkedCustomKey filter
        if (dataType === ASSESSMENTRUNQUESTION_INDEX) {
            const hasRegularFilter = filters?.[filterKey] !== undefined;
            const hasShouldFilter = filters?.should?.[filterKey] !== undefined;

            if (hasRegularFilter) query = transformEvaluatorFilter(query);
            if (hasShouldFilter) query.should = transformEvaluatorFilter(query.should);
        }

        if (isWorkflowOverlayOpen() || (!isAPlannerWorkspace && useEvaluatorIdFilter)) {
            commonFilteringService.appendFilterToEachNestedSet(query, currentEvaluatorFilter, ['evaluatorGrades', 'evaluatorScores']);
        }
        if (selectedCommitteeId > 0) {
            const committeeIdField = `${nestedPath}.committeeId`;
            const currentCommitteeFilter = commonSearchQueryBuilder.createNestedFilter(committeeIdField, selectedCommitteeId);
            commonFilteringService.appendFilterToEachNestedSet(query, currentCommitteeFilter,
                ['evaluatorGrades', 'evaluatorScores', 'assignments']);
        }
        if (assessment.isAssessmentPath
            && dataType === ASSESSMENTRUN_INDEX
            && filters.parentAssessmentRunId === 'parentAssessmentRunId') {
            filters.parentAssessmentRunId = assessmentRunId;
            query.parentAssessmentRunId = assessmentRunId;
        }
        if (dataType === GRADING_PARTY_INDEX && (selectedCommitteeId !== -1 || userIsGrader)) {
            const committeeIds: number[] = assessment.committees && assessment.committees.length ? assessment.committees
                .map((committee: any) => committee.committeeId) : null;
            let committeeFilter: any = {};
            if (selectedCommitteeId !== -1 && selectedCommitteeId > 0) {
                // when a committee is selected, search must include all current and previous candidates
                committeeFilter = {
                    should: {
                        'committeeId': [selectedCommitteeId],
                        'evaluationRounds.committeeId': [selectedCommitteeId],
                    },
                };
            } else {
                committeeFilter = {
                    committeeId: committeeIds,
                };
            }

            if (committeeFilter.should && (query.should || query.$nested)) {
                if (query.should && query.should.$nested) {
                    query.should.$nested = patchEvaluatorRoundsNestedFiltersWithCommittee(query.should.$nested, selectedCommitteeId);
                }
                if (query.$nested) {
                    query.$nested = patchEvaluatorRoundsNestedFiltersWithCommittee(query.$nested, selectedCommitteeId);
                }
                query = {
                    ...query,
                    // the committee filter must always be added regardless of other filters
                    must: { ...query.must, ...committeeFilter },
                };
            } else {
                query = {
                    ...query,
                    ...committeeFilter,
                };
            }
        }

        return query;
    },

    getFilters(dataType: string): any[] {
        switch (dataType) {
        case GRADING_PARTY_INDEX:
            return gradingPartyJson.filters;
        case USERQUESTION_INDEX:
            return userQuestionJson.filters;
        case ASSESSMENTRUNQUESTION_INDEX:
            return assessmentRunQuestionJson.filters;
        case GRADINGREADY_ISSUE:
            return allGradingReadyIssueJson.filters;
        case ASSESSMENTRUN_INDEX:
            return assessmentRunJson.filters;
        default:
            return [];
        }
    },

    getNestedPropertyPath(dataType: string): string {
        switch (dataType) {
        case GRADING_PARTY_INDEX:
            return 'evaluatorGrades';
        case USERQUESTION_INDEX:
            return 'evaluatorScores';
        case ASSESSMENTRUNQUESTION_INDEX:
            return 'assignments';
        default:
            return null;
        }
    },
};

export default filteringService;
